/**
 * Swish error code → translation text key mappings.
 *
 * Used by both user-web and admin-web to display localized error messages
 * when a Swish payment fails. The error codes come from the Swish Commerce API v2.
 *
 * Translation entries live in compact-texts.json under keys like "swishErrorACMT03".
 * The generic fallback is "registrationStep5PaymentSwishGenericError".
 *
 * Two categories:
 * 1. "Create" errors — returned synchronously (HTTP 422) when creating a payment request
 * 2. "Callback" errors — returned asynchronously via webhook after user interaction
 *
 * @see https://developer.swish.nu/api/mss/v1#create-payment-request-v2
 */

const GENERIC_ERROR_KEY = 'registrationStep5PaymentSwishGenericError'

/** Errors returned synchronously when creating a Swish payment request */
const SwishCreateErrorTextKeys: Record<string, string> = {
  BE18: 'swishErrorBE18',
  RP06: 'swishErrorRP06',
  ACMT03: 'swishErrorACMT03',
  ACMT01: 'registrationStep5PaymentSwishNotActivatedError',
  ACMT07: 'swishErrorACMT07',
  VR01: 'swishErrorVR01',
  VR02: 'swishErrorVR02'
}

/** Errors returned asynchronously via Swish webhook callback */
const SwishCallbackErrorTextKeys: Record<string, string> = {
  RF07: 'swishErrorRF07',
  BANKIDCL: 'swishErrorBANKIDCL',
  FF10: 'swishErrorFF10',
  TM01: 'swishErrorTM01',
  DS24: 'swishErrorDS24'
}

/**
 * Get the translation text key for a Swish error code.
 * Checks both create-time and callback error codes.
 *
 * @returns A compact-texts.json key, or the generic fallback if the code is unknown.
 */
export const getSwishErrorTextKey = (errorCode: string | undefined): string => {
  if (!errorCode) return GENERIC_ERROR_KEY

  return SwishCreateErrorTextKeys[errorCode] ?? SwishCallbackErrorTextKeys[errorCode] ?? GENERIC_ERROR_KEY
}

/**
 * Check if a Swish error code has a known specific translation.
 * When false, callers should show the generic message + the raw error code.
 */
export const isKnownSwishErrorCode = (errorCode: string | undefined): boolean => {
  if (!errorCode) return false
  return errorCode in SwishCreateErrorTextKeys || errorCode in SwishCallbackErrorTextKeys
}
